import * as React from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Button } from '@/components/display/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons';
import { AiChatModal } from '@/features/ai/ai-chat-modal';
import {
  TextBubble,
  SystemNoteLine,
  WorkspaceEmptyState,
  WorkspaceSuggestions,
  buildChatSegments,
  pendingWorkspaceQuestion
} from '@/features/ai/ai-chat-primitives';
import {
  extractWorkspaceData,
  googleAdsProposalToPreview,
  metaProposalToPreview,
  proposalToPreview
} from '@/features/ai/skills/workspace-card-registry';
import { MediaPlanPreviewCard } from '@/features/ai/skills/preview-card-registry';
import { SchemaEntityCard } from '@/features/ai/skills/schema-entity-card';
import { BeeswaxTargetingCard } from '@/features/ai/skills/beeswax/beeswax-targeting-card';
import { TaskChooser, type TaskOption } from '@/features/ai/shared/task-chooser';
import { externalCommitOutcome } from '@/features/ai/shared/commit-card-frame';
import { ResearchResultsCard } from './research-results-card';
import { CampaignContextCard } from '@/features/ai/line-items/campaign-context-card';
import { ZohoNotesCard } from '@/features/ai/shared/zoho-notes-card';
import {
  MediaPlanConfigurationCard,
  isSelectionComplete,
  type MediaPlanCardSelection,
  type MediaPlanInferredFromZoho
} from './media-plan-configuration-card';
import { ZohoMediaPlanHydrator } from './zoho-media-plan-hydrator';
import { client, trpc } from '@/utils/trpc-client';
import type { TypeaheadItem } from '@/components/forms/ui/AsyncTypeahead';
import type {
  MediaPlanChatMessage,
  MediaPlanWorkspaceConfigData,
  LinkData,
  CreateMediaPlanToolInput,
  CreateGoogleAdsMediaPlanToolInput,
  CreateMetaMediaPlanToolInput
} from 'shared/src/api/ai-media-plan-chat-types';
import type { AdapterCapabilities } from 'shared/src/domain/ai/skills/skill-platform-types';
import type { Platform } from 'shared/src/domain/line-item/line-item-channels';
import { BEESWAX_MEDIA_PLATFORM_ID } from 'shared/src/domain/media-platform/media-platforms';
import { getSkillPlatformConfig } from 'shared/src/domain/ai/skills/skill-platform-registry';
import type { WorkspaceHelpers } from '@/features/ai/ai-chat-modal';
import {
  resolveAssistMePlatforms,
  pickNextPlatform
} from '@/features/ai/skills/assist-me-platforms';
import { getMediaPlanConfigBehavior } from '@/features/ai/skills/media-plan-config-registry';

type Props = {
  executionLineItemId: string;
  strategyId: string;
  lineItemName: string;
  lineItemPlannedUnitCost: number;
  lineItemUnitPriceTypeName: string;
  lineItemGeo?: string;
  lineItemTacticName?: string;
  lineItemChannelName?: string;
  mediaPlatforms: Platform[];
  size?: 'default' | 'compact';
};

const EMPTY_SELECTION: MediaPlanCardSelection = { campaignMode: 'create' };

export function AiCreateMediaPlanButton({
  executionLineItemId,
  strategyId,
  lineItemName,
  lineItemPlannedUnitCost,
  lineItemUnitPriceTypeName,
  lineItemGeo,
  lineItemTacticName,
  lineItemChannelName,
  mediaPlatforms,
  size = 'compact'
}: Props) {
  const [activePlatformId, setActivePlatformId] = React.useState<number | null>(null);
  const [createdPlatformIds, setCreatedPlatformIds] = React.useState<number[]>([]);
  const utils = trpc.useUtils();

  const supportedPlatforms = React.useMemo(
    () => resolveAssistMePlatforms(mediaPlatforms),
    [mediaPlatforms]
  );

  if (supportedPlatforms.length === 0) return null;

  function handleOpen(platformId: number) {
    setActivePlatformId(platformId);
  }

  function handleClose() {
    setActivePlatformId(null);
    utils.invalidate();
  }

  function handleCreated(platformId: number) {
    setCreatedPlatformIds(prev => (prev.includes(platformId) ? prev : [...prev, platformId]));
  }

  function handleRequestNewPlatform(nextPlatformId: number) {
    setActivePlatformId(nextPlatformId);
  }

  return (
    <>
      {supportedPlatforms.length === 1 ? (
        <AssistMeButton size={size} onClick={() => handleOpen(supportedPlatforms[0].id)} />
      ) : (
        <AssistMePlatformMenu
          size={size}
          platforms={supportedPlatforms}
          onSelect={id => handleOpen(id)}
        />
      )}
      {activePlatformId !== null ? (
        <ChatSession
          key={activePlatformId}
          platformId={activePlatformId}
          executionLineItemId={executionLineItemId}
          strategyId={strategyId}
          lineItemName={lineItemName}
          lineItemPlannedUnitCost={lineItemPlannedUnitCost}
          lineItemUnitPriceTypeName={lineItemUnitPriceTypeName}
          lineItemGeo={lineItemGeo}
          lineItemTacticName={lineItemTacticName}
          lineItemChannelName={lineItemChannelName}
          supportedPlatforms={supportedPlatforms}
          createdPlatformIds={createdPlatformIds}
          onClose={handleClose}
          onCreated={handleCreated}
          onRequestNewPlatform={handleRequestNewPlatform}
        />
      ) : null}
    </>
  );
}

function AssistMeButton({ size, onClick }: { size: 'default' | 'compact'; onClick: () => void }) {
  return (
    <Button onClick={onClick} size={size}>
      <div className="flex items-center gap-2">
        <FontAwesomeIcon icon={faWandMagicSparkles} />
        <div>Assist me</div>
      </div>
    </Button>
  );
}

function AssistMePlatformMenu({
  size,
  platforms,
  onSelect
}: {
  size: 'default' | 'compact';
  platforms: Platform[];
  onSelect: (platformId: number) => void;
}) {
  return (
    <Menu as="div" className="relative">
      <MenuButton as={Button} size={size}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faWandMagicSparkles} />
          <div>Assist me</div>
        </div>
      </MenuButton>
      <MenuItems className="absolute right-0 z-20 mt-2 w-56 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
        <div className="px-3 py-1 text-xs text-gray-500">Choose a platform</div>
        {platforms.map(p => (
          <MenuItem key={p.id}>
            {({ focus }) => (
              <button
                type="button"
                onClick={() => onSelect(p.id)}
                className={`block w-full px-3 py-2 text-left text-sm ${
                  focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                }`}>
                {p.name}
              </button>
            )}
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}

type ChatSessionProps = {
  platformId: number;
  executionLineItemId: string;
  strategyId: string;
  lineItemName: string;
  lineItemPlannedUnitCost: number;
  lineItemUnitPriceTypeName: string;
  lineItemGeo?: string;
  lineItemTacticName?: string;
  lineItemChannelName?: string;
  supportedPlatforms: Platform[];
  createdPlatformIds: number[];
  onClose: () => void;
  onCreated: (platformId: number) => void;
  onRequestNewPlatform: (nextPlatformId: number) => void;
};

function ChatSession({
  platformId,
  executionLineItemId,
  strategyId,
  lineItemName,
  lineItemPlannedUnitCost,
  lineItemUnitPriceTypeName,
  lineItemGeo,
  lineItemTacticName,
  lineItemChannelName,
  supportedPlatforms,
  createdPlatformIds,
  onClose,
  onCreated,
  onRequestNewPlatform
}: ChatSessionProps) {
  const adapterConfig = getSkillPlatformConfig(platformId);
  const remainingPlatforms = supportedPlatforms.filter(
    p => !createdPlatformIds.includes(p.id) && p.id !== platformId
  );
  const nextPlatform = pickNextPlatform(remainingPlatforms, platformId);

  const [selection, setSelection] = React.useState<MediaPlanCardSelection>(EMPTY_SELECTION);
  const [inferredFromZoho, setInferredFromZoho] = React.useState<MediaPlanInferredFromZoho>({});
  const [createdLinks, setCreatedLinks] = React.useState<LinkData[]>([]);
  const [finalizedKey, setFinalizedKey] = React.useState<string | null>(null);
  // null = task-selection landing; 'create' shows the configuration card; 'agent' = a manage task or
  // freeform, so the agent drives via tools (no create card). Resets on remount (keyed by platform).
  const [mode, setMode] = React.useState<'create' | 'agent' | null>(null);
  const hydratedFor = React.useRef<string | null>(null);

  const lineItem = React.useMemo(
    () => ({
      planned_unit_cost: lineItemPlannedUnitCost,
      unit_price_type_name: lineItemUnitPriceTypeName,
      geo: lineItemGeo,
      tactic_name: lineItemTacticName,
      channel_name: lineItemChannelName
    }),
    [
      lineItemPlannedUnitCost,
      lineItemUnitPriceTypeName,
      lineItemGeo,
      lineItemTacticName,
      lineItemChannelName
    ]
  );

  // Account picker search: campaign-used accounts first (deduped from the DSP list), manager
  // accounts dropped (they cannot host campaigns). Selecting an item pins its id on the card.
  const searchDspAccounts = React.useCallback(
    async (search: string): Promise<TypeaheadItem[]> => {
      const result = await client.discoverDspAccounts.query({
        platform_id: platformId,
        strategy_id: strategyId,
        search,
        manager_account_id: selection.managerAccountId
      });
      // A DSP failure with campaign-used accounts still shows those; with nothing at all to show,
      // surface the failure in the dropdown instead of a misleading empty "No matches".
      if (
        result.dsp_error &&
        result.used_in_this_campaign.length === 0 &&
        result.available_in_dsp.length === 0
      ) {
        throw new Error(result.dsp_error);
      }
      const usedIds = new Set(result.used_in_this_campaign.map(a => a.id));
      return [
        ...result.used_in_this_campaign.map(a => ({
          id: a.id,
          label: `${a.name} (${a.id}) — used in this campaign`
        })),
        ...result.available_in_dsp
          .filter(a => !a.is_manager && !usedIds.has(a.id))
          .map(a => ({ id: a.id, label: `${a.name} (${a.id})` }))
      ];
    },
    [platformId, strategyId, selection.managerAccountId]
  );

  const searchDspPages = React.useCallback(
    async (search: string): Promise<TypeaheadItem[]> => {
      const result = await client.discoverDspPages.query({ platform_id: platformId, search });
      if (result.dsp_error && result.pages.length === 0) {
        throw new Error(result.dsp_error);
      }
      return result.pages.map(p => ({ id: p.id, label: `${p.name} (${p.id})` }));
    },
    [platformId]
  );

  // Whether the configuration card is currently mounted; kept fresh by renderWorkspaceContent and
  // read at send time so every chat request carries the true card-visibility state.
  const cardVisibleRef = React.useRef(false);
  const getExtraBody = React.useCallback(
    () => ({ config_card_visible: cardVisibleRef.current }),
    []
  );

  const renderChatContent = React.useCallback((message: MediaPlanChatMessage) => {
    // Chat shows conversation only (text + status notes). Actionable choices (presentOptions) render
    // in the workspace via WorkspaceSuggestions.
    const segments = buildChatSegments(message.parts);
    return segments.map((seg, si) => {
      if (seg.type === 'text') {
        return <TextBubble key={`text-${si}`} texts={seg.texts} />;
      }
      if (seg.type === 'system-note') {
        return <SystemNoteLine key={`sys-${si}`} text={seg.text} />;
      }
      return null;
    });
  }, []);

  const renderWorkspaceContent = React.useCallback(
    (allMessages: MediaPlanChatMessage[], helpers: WorkspaceHelpers) => {
      const {
        campaignContext,
        workspaceConfig,
        latestPreview,
        latestCreateCall,
        latestMutateCall,
        latestTargetingCall,
        latestGoogleAdsProposal,
        latestMetaProposal,
        latestResearch,
        latestResearchId,
        latestEntityCard,
        latestEntityCardId,
        links,
        configCardRequested,
        proposalAfterCreate
      } = extractWorkspaceData(allMessages);
      // The agent can mount the configuration card mid-conversation (showConfigurationCard) for
      // freeform sessions where the task chooser was never clicked.
      const effectiveMode = configCardRequested ? 'create' : mode;
      // Live card-visibility signal for the chat request body (read at send time via getExtraBody):
      // the backend prompt branches on whether the configuration card is actually on screen.
      cardVisibleRef.current = effectiveMode === 'create' && Boolean(workspaceConfig);

      const capabilities = workspaceConfig?.capabilities;
      // Line-item price drives the campaign-split budget reconciliation (campaignContext carries it
      // as proposal_budget). Undefined when no campaign context resolved — then the split sum is not
      // gated client-side (the server still emits a budget_mismatch notice at preview).
      const lineItemPrice = campaignContext?.proposal_budget;

      const handleCardChange = (next: MediaPlanCardSelection) => {
        setSelection(next);
        setInferredFromZoho(prev => clearOverriddenBadges(prev, selection, next));
        // Editing any field after finalize re-arms the button so the user can re-submit.
        const nextKey = selectionKey(next, capabilities);
        if (finalizedKey !== null && nextKey !== finalizedKey) {
          setFinalizedKey(null);
        }
      };

      const handleFinalize = () => {
        const key = selectionKey(selection, capabilities);
        if (!key || helpers.isBusy) return;
        setFinalizedKey(key);
        helpers.sendMessage(getMediaPlanConfigBehavior(platformId).buildProposePrompt(selection));
      };

      // The chooser persists until a task/mode is active (clicked, or the agent mounted the config
      // card) — typing freeform no longer dismisses it, so the tasks stay one click away.
      const showChooser = effectiveMode === null && Boolean(workspaceConfig);
      const pending = pendingWorkspaceQuestion(allMessages, helpers.isBusy);

      const handleTaskSelect = (taskId: string) => {
        if (taskId === 'create') {
          setMode('create');
          // sendMessage reads getExtraBody synchronously, before the re-render that would refresh
          // the ref, so mark the card visible now or this request reports it as hidden.
          cardVisibleRef.current = Boolean(workspaceConfig);
          helpers.sendMessage('I want to create a media plan.');
          return;
        }
        setMode('agent');
        helpers.sendMessage(mediaPlanTaskSeed(taskId, workspaceConfig?.linked_media_plan));
      };

      if (
        !campaignContext &&
        !workspaceConfig &&
        !latestPreview &&
        !latestCreateCall &&
        !latestMutateCall &&
        !latestTargetingCall &&
        !latestEntityCard &&
        links.length === 0
      ) {
        return <WorkspaceEmptyState stepLabel="Waiting for AI to gather context" />;
      }

      // Confirmed-write (Beeswax / Google Ads / Meta): render the preview card from the create tool
      // call and resolve the tool on every terminal outcome. Unported platforms use the legacy part.
      const previewData = latestCreateCall
        ? latestCreateCall.tool === 'createMediaPlan'
          ? proposalToPreview(
              latestCreateCall.input as CreateMediaPlanToolInput,
              latestCreateCall.toolCallId,
              executionLineItemId
            )
          : latestCreateCall.tool === 'createMetaMediaPlan'
            ? metaProposalToPreview(
                latestCreateCall.input as CreateMetaMediaPlanToolInput,
                latestCreateCall.toolCallId,
                executionLineItemId,
                latestMetaProposal
              )
            : googleAdsProposalToPreview(
                latestCreateCall.input as CreateGoogleAdsMediaPlanToolInput,
                latestCreateCall.toolCallId,
                executionLineItemId,
                latestGoogleAdsProposal
              )
        : latestPreview;

      const hydrationKey = workspaceConfig
        ? [
            platformId,
            workspaceConfig.account_id_prefill ?? '',
            workspaceConfig.zoho_geo.join(','),
            workspaceConfig.zoho_audience.join(','),
            workspaceConfig.zoho_description ?? '',
            workspaceConfig.zoho_notes ?? ''
          ].join('|')
        : '';

      // Focus takeover: while a confirmed-write card is pending the user's decision, it is the
      // only interactive surface — everything else dims and stops receiving pointer events. The
      // legacy (no-tool-call) preview is exempt: its resolution isn't visible at this level.
      const previewPending = latestCreateCall?.state === 'input-available';
      const mutatePending = latestMutateCall?.state === 'input-available';
      const targetingPending = latestTargetingCall?.state === 'input-available';
      const commitPending = Boolean(previewPending || mutatePending || targetingPending);
      const dimClass = commitPending ? 'pointer-events-none select-none opacity-40' : '';

      return (
        <div className="space-y-4">
          <div className={`space-y-4 ${dimClass}`}>
            {campaignContext ? <CampaignContextCard data={campaignContext} /> : null}
            {campaignContext ? <ZohoNotesCard notes={campaignContext.zoho_notes_module} /> : null}
            {showChooser && workspaceConfig ? (
              <TaskChooser
                options={mediaPlanTaskOptions(platformId, workspaceConfig.linked_media_plan)}
                onSelect={handleTaskSelect}
              />
            ) : null}
          </div>
          {effectiveMode === 'create' && workspaceConfig ? (
            <ZohoMediaPlanHydrator
              capabilities={workspaceConfig.capabilities}
              accountIdPrefill={workspaceConfig.account_id_prefill}
              lineItem={lineItem}
              zohoGeo={workspaceConfig.zoho_geo}
              zohoAudience={workspaceConfig.zoho_audience}
              zohoDescription={workspaceConfig.zoho_description}
              zohoNotes={workspaceConfig.zoho_notes}
              lineItemPrice={lineItemPrice}
              hydrationKey={hydrationKey}
              setSelection={setSelection}
              setInferredFromZoho={setInferredFromZoho}
              hydratedFor={hydratedFor}
            />
          ) : null}
          <div className={`space-y-4 ${dimClass}`}>
            {effectiveMode === 'create' && workspaceConfig ? (
              <MediaPlanConfigurationCard
                capabilities={workspaceConfig.capabilities}
                platformName={adapterConfig.config.platformName}
                selection={selection}
                isBusy={helpers.isBusy}
                inferredFromZoho={inferredFromZoho}
                onChange={handleCardChange}
                onFinalize={handleFinalize}
                lineItemPrice={lineItemPrice}
                onSearchAccounts={searchDspAccounts}
                onSearchPages={capabilities?.requiresPageId ? searchDspPages : undefined}
                hasFinalized={
                  finalizedKey !== null && finalizedKey === selectionKey(selection, capabilities)
                }
              />
            ) : null}
            {latestResearch ? (
              <ResearchResultsCard
                key={latestResearchId ?? 'research'}
                data={latestResearch}
                mediaBuyEntityType={adapterConfig.config.mediaBuyEntityType}
                platformShortLabel={adapterConfig.config.platformShortLabel}
              />
            ) : null}
            {latestEntityCard
              ? latestEntityCard.rows.map((row, i) => (
                  <SchemaEntityCard
                    key={`entity-${latestEntityCardId ?? 'card'}-${i}`}
                    mode="readonly"
                    entityType={latestEntityCard.entityType}
                    data={row}
                    title={
                      latestEntityCard.entityId
                        ? `${latestEntityCard.entityType} #${latestEntityCard.entityId}`
                        : latestEntityCard.entityType
                    }
                  />
                ))
              : null}
          </div>
          {previewData ? (
            <div className={commitPending && !previewPending ? dimClass : undefined}>
              <MediaPlanPreviewCard
                // Include the latest propose call id so a bare re-propose (new resolved entities,
                // unchanged create call) remounts the card and re-seeds its editable fields with the
                // updated dates/budget/name — the BRVM-61 fix. Beeswax has no propose call, so it
                // falls back to the create call id alone.
                key={`${latestCreateCall?.toolCallId ?? 'legacy-preview'}:${
                  latestGoogleAdsProposal?.toolCallId ?? latestMetaProposal?.toolCallId ?? ''
                }`}
                data={previewData}
                strategyId={strategyId}
                lineItemName={lineItemName}
                onFieldsEdited={() => {
                  // Field edits on the preview card are no longer routed through pending-edits;
                  // the user re-runs the finalize flow if they want different values.
                }}
                onCreated={() => onCreated(platformId)}
                onCreatedLinks={newLinks =>
                  setCreatedLinks(prev => {
                    const seen = new Set(prev.map(l => l.url));
                    const fresh = newLinks.filter(l => !seen.has(l.url));
                    return fresh.length ? [...prev, ...fresh] : prev;
                  })
                }
                nextPlatform={nextPlatform}
                onRequestNewPlatform={onRequestNewPlatform}
                toolCallId={latestCreateCall?.toolCallId}
                externalOutcome={
                  latestCreateCall ? externalCommitOutcome(latestCreateCall) : undefined
                }
                // Cancelled or failed (not applied, and not still pending, which reads as null).
                updatedAfterResolve={Boolean(
                  latestCreateCall &&
                    proposalAfterCreate &&
                    ['cancelled', 'failed'].includes(
                      externalCommitOutcome(latestCreateCall)?.status ?? ''
                    )
                )}
                onResolve={
                  latestCreateCall
                    ? output => {
                        output.status === 'failed'
                          ? helpers.addToolOutput({
                              tool: latestCreateCall.tool,
                              toolCallId: latestCreateCall.toolCallId,
                              state: 'output-error',
                              errorText: output.error ?? 'Failed to create media plan'
                            })
                          : helpers.addToolOutput({
                              tool: latestCreateCall.tool,
                              toolCallId: latestCreateCall.toolCallId,
                              state: 'output-available',
                              output
                            });
                      }
                    : undefined
                }
              />
            </div>
          ) : null}
          {latestMutateCall ? (
            <div className={commitPending && !mutatePending ? dimClass : undefined}>
              <SchemaEntityCard
                mode="editable"
                key={latestMutateCall.toolCallId}
                tool={latestMutateCall.tool}
                toolCallId={latestMutateCall.toolCallId}
                input={latestMutateCall.input}
                platformId={platformId}
                executionLineItemId={executionLineItemId}
                strategyId={strategyId}
                externalOutcome={externalCommitOutcome(latestMutateCall)}
                onResolve={output =>
                  output.status === 'failed'
                    ? helpers.addToolOutput({
                        tool: latestMutateCall.tool,
                        toolCallId: latestMutateCall.toolCallId,
                        state: 'output-error',
                        errorText: output.error ?? 'Failed to apply the change'
                      })
                    : helpers.addToolOutput({
                        tool: latestMutateCall.tool,
                        toolCallId: latestMutateCall.toolCallId,
                        state: 'output-available',
                        output
                      })
                }
              />
            </div>
          ) : null}
          {latestTargetingCall ? (
            <div className={commitPending && !targetingPending ? dimClass : undefined}>
              <BeeswaxTargetingCard
                key={latestTargetingCall.toolCallId}
                toolCallId={latestTargetingCall.toolCallId}
                input={latestTargetingCall.input}
                platformId={platformId}
                executionLineItemId={executionLineItemId}
                strategyId={strategyId}
                externalOutcome={externalCommitOutcome(latestTargetingCall)}
                onResolve={output =>
                  output.status === 'failed'
                    ? helpers.addToolOutput({
                        tool: 'setLineItemTargeting',
                        toolCallId: latestTargetingCall.toolCallId,
                        state: 'output-error',
                        errorText: output.error ?? 'Failed to set targeting'
                      })
                    : helpers.addToolOutput({
                        tool: 'setLineItemTargeting',
                        toolCallId: latestTargetingCall.toolCallId,
                        state: 'output-available',
                        output
                      })
                }
              />
            </div>
          ) : null}
          <div className={`space-y-4 ${dimClass}`}>
            {/* Created-entity links first, from the create result (authoritative names). Then any
                model showLink links, minus ones already shown here (deduped by url) so an edited
                campaign name is never duplicated by the model's stale-named link (BRVM-74). */}
            {createdLinks.map((link, i) => (
              <WorkspaceLinkCard key={`created-link-${i}`} data={link} />
            ))}
            {links
              .filter(link => !createdLinks.some(c => c.url === link.url))
              .map((link, i) => (
                <WorkspaceLinkCard key={`link-${i}`} data={link} />
              ))}
            {pending ? (
              <WorkspaceSuggestions
                id={pending.id}
                question={pending.question}
                options={pending.options}
                multiSelect={pending.multiSelect}
                onSelect={value =>
                  value === 'Close' ? helpers.onClose() : helpers.sendMessage(value)
                }
                disabled={helpers.isBusy || commitPending}
              />
            ) : null}
          </div>
        </div>
      );
    },
    [
      strategyId,
      lineItemName,
      adapterConfig.config.mediaBuyEntityType,
      adapterConfig.config.platformShortLabel,
      adapterConfig.config.platformName,
      onCreated,
      nextPlatform,
      onRequestNewPlatform,
      platformId,
      selection,
      inferredFromZoho,
      lineItem,
      finalizedKey,
      executionLineItemId,
      mode,
      searchDspAccounts,
      searchDspPages,
      createdLinks
      // latestEntityCard is derived from allMessages (a render arg), not closure state.
    ]
  );

  return (
    <AiChatModal<MediaPlanChatMessage>
      open={true}
      onClose={onClose}
      title={`Assist Me · ${lineItemName} — ${adapterConfig.config.platformName}`}
      steps={adapterConfig.config.workflowSteps}
      apiEndpoint="/api/media-plan-chat"
      body={{
        execution_line_item_id: executionLineItemId,
        strategy_id: strategyId,
        platform_id: platformId
      }}
      getExtraBody={getExtraBody}
      initialMessage="Greet me briefly and show me what I can do for this line item."
      renderChatContent={renderChatContent}
      renderWorkspaceContent={renderWorkspaceContent}
      commitTools={MEDIA_PLAN_COMMIT_TOOLS}
      cancelledOutputFor={cancelledWriteResult}
    />
  );
}

// The confirmed-write tools whose pending card is a commit moment — the modal's soft lock cancels
// the pending one (with a cancelled write result) when the user keeps typing instead.
const MEDIA_PLAN_COMMIT_TOOLS = [
  'createMediaPlan',
  'createGoogleAdsMediaPlan',
  'createMetaMediaPlan',
  'setEntityStatus',
  'updateEntity',
  'setLineItemTargeting'
];

// 'superseded' tells the agent this was the modal's soft-cancel — the user's message follows
// immediately, so it should wait for it rather than asking what to change or re-proposing.
function cancelledWriteResult(): { status: 'cancelled'; reason: 'superseded' } {
  return { status: 'cancelled', reason: 'superseded' };
}

type LinkedMediaPlan = NonNullable<MediaPlanWorkspaceConfigData['linked_media_plan']>;

// Seed message for a (non-create) task, referencing the existing media plan's entity when known so the
// agent acts on the right one instead of asking which.
function mediaPlanTaskSeed(taskId: string, linked: LinkedMediaPlan | undefined): string {
  const target = linked
    ? ` Use ${linked.entity_type} ${linked.entity_id}` +
      (linked.campaign_id ? ` (campaign ${linked.campaign_id})` : '') +
      ' — the entity in this line item’s existing media plan.'
    : ' Look up the entity first.';
  if (taskId === 'targeting') {
    return `I want to set or replace the targeting on this line item’s media plan.${target}`;
  }
  if (taskId === 'manage') {
    return `I want to inspect or manage this line item’s existing media plan.${target}`;
  }
  return taskId;
}

// Context-derived task options for the landing chooser. Manage / targeting are offered when this line
// item already has a media plan on this platform (so there is a concrete entity to act on).
function mediaPlanTaskOptions(
  platformId: number,
  linked: LinkedMediaPlan | undefined
): TaskOption[] {
  const options: TaskOption[] = [
    {
      id: 'create',
      label: 'Create a media plan',
      description: 'Set up a new campaign + line item for this execution line item.'
    }
  ];
  if (linked) {
    options.push({
      id: 'manage',
      label: 'Inspect or manage the existing media plan',
      description: 'Browse, pause, or update the campaign / line item already linked here.'
    });
    // Targeting writes are Beeswax-only in this version.
    if (platformId === BEESWAX_MEDIA_PLATFORM_ID) {
      options.push({
        id: 'targeting',
        label: 'Set or replace targeting',
        description: 'Apply a targeting expression to this line item’s media plan.'
      });
    }
  }
  return options;
}

// The card's own readiness gate decides whether a key exists, so the finalize button can never look
// Ready yet no-op here (or the reverse) as the per-platform gates evolve.
function selectionKey(
  s: MediaPlanCardSelection,
  capabilities: AdapterCapabilities | undefined
): string | null {
  if (!capabilities || !isSelectionComplete(s, capabilities)) {
    return null;
  }
  return [
    s.accountId ?? '',
    s.pageId ?? '',
    (s.specialAdCategories ?? []).join(','),
    s.ageMin ?? '',
    s.campaignMode,
    s.campaignId ?? '',
    s.lineItemType,
    s.budgetType,
    s.biddingStrategy,
    s.targetCpa ?? '',
    s.targetRoas ?? '',
    s.pacing ?? '',
    s.cpmBid ?? '',
    s.maxCpc ?? '',
    s.tracking ?? '',
    (s.geo ?? []).join(','),
    JSON.stringify(s.campaigns ?? [])
  ].join('|');
}

function clearOverriddenBadges(
  prev: MediaPlanInferredFromZoho,
  prevSelection: MediaPlanCardSelection,
  nextSelection: MediaPlanCardSelection
): MediaPlanInferredFromZoho {
  const next = { ...prev };
  if (nextSelection.lineItemType !== prevSelection.lineItemType) next.lineItemType = false;
  const sameGeo =
    [...(prevSelection.geo ?? [])].sort().join(',') ===
    [...(nextSelection.geo ?? [])].sort().join(',');
  if (!sameGeo) next.geo = false;
  return next;
}

function WorkspaceLinkCard({ data }: { data: LinkData }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-lg border bg-white px-4 py-3 shadow-sm">
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm font-medium text-primary hover:underline">
        {data.label} &#x2197;
      </a>
      {data.description ? <span className="text-xs text-gray-500">{data.description}</span> : null}
    </div>
  );
}
